<script setup lang="ts">
import {
  Clock,
  QrCode,
  ShieldCheck,
} from "@lucide/vue";
import {
  useAgreementDialog,
} from "~/services/agreement-dialog";
import {
  AGREEMENT_TYPE,
} from "~/types/agreement";

interface PaymentInformationItem {
  icon: "clock" | "qr";
  title?: string;
  text: string;
}

const props = defineProps<{
  modelValue: boolean;
  showValidationError?: boolean;
  informationItems: PaymentInformationItem[];
  purchaseKind: "credit_package" | "plan";
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
}>();

const {
  $t,
} = useNuxtApp();
const localeStore = useLocaleStore();

const termsParts = computed(() => $t(props.purchaseKind === "credit_package"
  ? "PAYMENT_CHECKOUT.TERMS_ACCEPTANCE_CREDIT"
  : "PAYMENT_CHECKOUT.TERMS_ACCEPTANCE_PLAN").split("{terms}"));
const privacyParts = computed(() => $t("PAYMENT_CHECKOUT.PRIVACY_INFO").split("{privacy}"));

const openAgreement = async (type: typeof AGREEMENT_TYPE[keyof typeof AGREEMENT_TYPE]) => {
  await useAgreementDialog(type, localeStore.currentLocale, {
    readOnly: true,
  });
};
</script>

<template>
  <div class="space-y-3">
    <div
      class="rounded-xl border p-4 text-left transition-colors"
      :class="showValidationError && !modelValue
        ? 'border-red-500 bg-red-50 dark:border-red-500 dark:bg-red-950/20'
        : 'border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-800/50'"
    >
      <el-checkbox
        :model-value="modelValue"
        class="!h-auto !items-start whitespace-normal"
        @update:model-value="emit('update:modelValue', Boolean($event))"
      >
        <span class="whitespace-normal text-sm leading-relaxed text-gray-700 dark:text-gray-200">
          {{ termsParts[0] }}
          <button
            type="button"
            class="font-semibold text-primary-600 underline underline-offset-2 hover:text-primary-700 dark:text-primary-400"
            @click.stop="openAgreement(AGREEMENT_TYPE.TERMS_AND_CONDITIONS)"
          >
            {{ $t("PAYMENT_CHECKOUT.TERMS_LINK") }}
          </button>
          {{ termsParts[1] }}
        </span>
      </el-checkbox>
      <p
        v-if="showValidationError && !modelValue"
        class="mt-2 text-sm font-medium text-red-600 dark:text-red-400"
      >
        {{ $t("PAYMENT_CHECKOUT.TERMS_REQUIRED") }}
      </p>
    </div>

    <slot name="actions" />

    <div class="divide-y divide-blue-200 overflow-hidden rounded-xl border border-blue-200 bg-blue-50/70 dark:divide-blue-900/60 dark:border-blue-900/60 dark:bg-blue-950/20">
      <div
        v-for="(item, index) in informationItems"
        :key="index"
        class="flex gap-3 p-4 text-left"
      >
        <Clock
          v-if="item.icon === 'clock'"
          class="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400"
        />
        <QrCode
          v-else
          class="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400"
        />
        <div>
          <div
            v-if="item.title"
            class="text-sm font-semibold text-blue-950 dark:text-blue-100"
          >
            {{ item.title }}
          </div>
          <p class="mt-1 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
            {{ item.text }}
          </p>
        </div>
      </div>

      <div class="flex gap-3 p-4 text-left">
        <ShieldCheck class="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
        <p class="text-sm leading-relaxed text-blue-800 dark:text-blue-200">
          {{ privacyParts[0] }}
          <button
            type="button"
            class="font-semibold underline underline-offset-2 hover:text-blue-950 dark:hover:text-white"
            @click="openAgreement(AGREEMENT_TYPE.PRIVACY_POLICY)"
          >
            {{ $t("PAYMENT_CHECKOUT.PRIVACY_LINK") }}
          </button>
          {{ privacyParts[1] }}
        </p>
      </div>
    </div>
  </div>
</template>
